import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator';
import { AudienceStatus } from '@openx/types/targeting/audience';
import { useTranslation } from 'react-i18next';
import type { GetAudiencesListQuery } from '../../../../../graphql/query/audiences/GetAudiencesList.generated';
import { ExportingStatus } from '../../constants';
import type { AudienceComputedStatus } from '../../utils/mapStatus';

const exportStatuses = [
  ExportingStatus.INIT,
  ExportingStatus.EXPORT_WORKER_DONE,
  ExportingStatus.EXPORT_DOWNLOADING,
  ExportingStatus.EXPORT_DOWNLOADING_DONE,
  ExportingStatus.EXPORT_PROCESSING,
  ExportingStatus.EXPORT_PROCESSING_DONE,
] as const;

const barsMapping = [
  ExportingStatus.EXPORT_WORKER_DONE,
  ExportingStatus.EXPORT_DOWNLOADING_DONE,
  ExportingStatus.EXPORT_PROCESSING_DONE,
] as const;

type StatusCellProps = {
  status: AudienceComputedStatus;
  label: string;
  lastExport: GetAudiencesListQuery['audience'][number]['last_export'];
};

export const StatusCell = ({ label, status, lastExport }: StatusCellProps) => {
  const { t } = useTranslation();
  const showProgress = label.toLowerCase() === AudienceStatus.EXPORTING;
  const exportStatus = lastExport?.status ?? '';

  return (
    <>
      <StatusIndicator status={status} label={label} />

      {showProgress && (
        <Tooltip
          title={t(
            'Audience is currently exporting, which may take multiple hours to complete. Please refresh the page for an updated status.'
          )}
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '62px',
              marginLeft: 2,
            }}
          >
            {barsMapping.map(stepStatus => {
              const isDone =
                (exportStatuses as readonly string[]).indexOf(exportStatus) >= exportStatuses.indexOf(stepStatus);

              return (
                <Box
                  component="span"
                  key={stepStatus}
                  sx={{
                    height: '6px',
                    width: '30%',
                    backgroundColor: theme =>
                      isDone ? theme.palette.primary.main : theme.palette.background.boxDarkest,
                  }}
                />
              );
            })}
          </Box>
        </Tooltip>
      )}
    </>
  );
};
